import React from "react";
import { Box } from "@mantine/core";

type OverlayIntensity = "none" | "light" | "medium" | "strong";

interface PageBackgroundPhotoProps {
  /** Image URL. If missing/empty, the layer renders nothing. */
  src?: string;
  alt?: string;
  /** Overall opacity of the photo layer. Default 0.22. */
  opacity?: number;
  /** CSS object-position — where the crop focuses. Default "center". */
  position?: string;
  /** Extra filter beyond the built-in grayscale+contrast. */
  filter?: string;
  /**
   * Radial dark gradient overlay that darkens the center (where content
   * lives) and fades to the edges (where the photo shows through).
   * Preserves photo character while keeping text contrast predictable.
   * Default "medium".
   */
  overlayIntensity?: OverlayIntensity;
}

const OVERLAY_STOPS: Record<OverlayIntensity, string> = {
  none: "transparent 0%",
  light:
    "rgba(15, 11, 8, 0.45) 0%, rgba(15, 11, 8, 0.20) 60%, rgba(15, 11, 8, 0) 100%",
  medium:
    "rgba(15, 11, 8, 0.72) 0%, rgba(15, 11, 8, 0.42) 55%, rgba(15, 11, 8, 0.05) 100%",
  strong:
    "rgba(15, 11, 8, 0.85) 0%, rgba(15, 11, 8, 0.55) 55%, rgba(15, 11, 8, 0.15) 100%",
};

/**
 * PageBackgroundPhoto - Subtle full-page photo layer with contrast overlay.
 *
 * Designed as a `PageShell.extraBackgrounds` prop input for pillar pages.
 * Photo is fixed-position (stays put during scroll like the mesh gradient),
 * grayscaled + dimmed. A radial dark overlay sits on top, darkening the
 * center where content lives while letting the photo peek through at the
 * edges — the standard editorial "photo behind text" technique.
 *
 * Silently renders nothing if `src` is empty.
 */
export const PageBackgroundPhoto: React.FC<PageBackgroundPhotoProps> = ({
  src,
  alt = "",
  opacity = 0.22,
  position = "center",
  filter,
  overlayIntensity = "medium",
}) => {
  if (!src) return null;

  const combinedFilter = filter
    ? `grayscale(90%) contrast(1.05) ${filter}`
    : "grayscale(90%) contrast(1.05)";

  return (
    <Box
      aria-hidden={alt === ""}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 0,
        overflow: "hidden",
        pointerEvents: "none",
      }}
    >
      <img
        src={src}
        alt={alt}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: position,
          filter: combinedFilter,
          opacity,
        }}
      />
      {overlayIntensity !== "none" && (
        <Box
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: `radial-gradient(ellipse at center, ${OVERLAY_STOPS[overlayIntensity]})`,
            pointerEvents: "none",
          }}
        />
      )}
    </Box>
  );
};

export default PageBackgroundPhoto;
